import React, { useEffect, useMemo, useState } from 'react';
import { getAuthHeader, getUserInfo } from '../utils/tokenManager';

const API_BASE_URL = 'https://trpliveapi1.tripbox.co.kr';

const formatDate = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const addDays = (d, days) => {
  const nd = new Date(d);
  nd.setDate(nd.getDate() + days);
  return nd;
};

const numberFmt = (v) => {
  if (v === null || v === undefined || v === '') return '';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString();
};

const columns = [
  { key: '거래일시', label: '거래일시', width: 'w-36' },
  { key: '피지사', label: 'PG사', width: 'w-28' },
  { key: '결제종류', label: '결제종류', width: 'w-24' },
  { key: '결제방법', label: '결제방법', width: 'w-24' },
  { key: '거래상태', label: '거래상태', width: 'w-20' },
  { key: '거래금액', label: '거래금액', width: 'w-28', isAmount: true },
  { key: '정산예정금액', label: '정산예정금액', width: 'w-28', isAmount: true },
  { key: '피지수수료', label: '피지수수료', width: 'w-24', isAmount: true },
  { key: '정산예정일', label: '정산예정일', width: 'w-28' },
  { key: '할부', label: '할부', width: 'w-16' },
  { key: '카드승인번호', label: '카드승인번호', width: 'w-28' },
  { key: '결제번호', label: '결제번호', width: 'w-24' },
  { key: '거래번호', label: '거래번호', width: 'w-[28rem]', truncate: true },
  { key: '주문번호', label: '주문번호', width: 'w-24' },
  { key: '출발일', label: '출발일', width: 'w-28' },
  { key: '상품명', label: '상품명', width: 'min-w-[20rem]', truncate: true },
  { key: '행사단체번호', label: '행사단체번호', width: 'w-28' },
];

const PGTransactionHistory = () => {
  const today = useMemo(() => new Date(), []);
  const [startDate, setStartDate] = useState(formatDate(addDays(today, -90)));
  const [endDate, setEndDate] = useState(formatDate(today));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const totals = useMemo(() => {
    const sum = (key) => rows.reduce((acc, r) => {
      const v = Number(r?.[key] ?? 0);
      return acc + (Number.isFinite(v) ? v : 0);
    }, 0);
    return {
      거래금액: sum('거래금액'),
      정산예정금액: sum('정산예정금액'),
      피지수수료: sum('피지수수료'),
    };
  }, [rows]);

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const { companyNo } = getUserInfo() || {};
      const payload = {
        Table1: [
          { 컬럼명: '@공급업체번호', 값: String(companyNo ?? ''), 리스트값: null },
          { 컬럼명: '@시작일', 값: startDate, 리스트값: null },
          { 컬럼명: '@종료일', 값: endDate, 리스트값: null },
        ],
      };
      const url = `${API_BASE_URL}/ERP/GetDataSet?procedureName=DBO.RCT_S_PG%EA%B1%B0%EB%9E%98%EB%82%B4%EC%97%AD`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`API error: ${res.status}`);
      const data = await res.json();
      const candidates = [data?.table, data?.Table, data?.table1, data?.Table1, data?.Table0];
      let list = [];
      for (const c of candidates) {
        if (Array.isArray(c)) { list = c; break; }
      }
      setRows(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
      setError('조회 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="space-y-3">
      <div className="rounded-md p-3 text-sm text-gray-800 flex items-end gap-3 flex-wrap">
        <div className="flex flex-col">
          <label className="text-xs text-gray-700 mb-1">시작일</label>
          <input type="date" className="border rounded px-2 py-1 text-sm" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </div>
        <div className="flex flex-col">
          <label className="text-xs text-gray-700 mb-1">종료일</label>
          <input type="date" className="border rounded px-2 py-1 text-sm" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </div>
        <button onClick={load} className="ml-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm">조회</button>
        
      </div>

      {loading && (
        <div className="p-6 text-center text-gray-600 text-sm">불러오는 중...</div>
      )}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded text-sm">{error}</div>
      )}

      {!loading && !error && (
        <>
          <div className="flex items-center justify-between text-sm text-gray-700">
            <div>건수: <span className="font-semibold">{rows.length.toLocaleString()}</span></div>
            <div className="flex gap-4">
              <div>총 거래금액: <span className="font-semibold">{numberFmt(totals.거래금액)}</span></div>
              <div>총 정산예정금액: <span className="font-semibold">{numberFmt(totals.정산예정금액)}</span></div>
              <div>총 피지수수료: <span className="font-semibold">{numberFmt(totals.피지수수료)}</span></div>
            </div>
          </div>
          <div className="border rounded-md overflow-auto max-h-[75vh]">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 z-10 bg-white text-gray-700 border-b shadow-sm">
              <tr>
                {columns.map(col => (
                  <th key={col.key} className={`px-3 py-4 text-center font-medium border-b whitespace-nowrap ${col.width || ''}`}>{col.label}</th>
                ))}
              </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={columns.length} className="px-3 py-6 text-center text-gray-500">데이터가 없습니다.</td>
                  </tr>
                )}
                {rows.map((row, idx) => (
                  <tr key={idx} className={`border-b ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50/40`}>
                    {columns.map(col => {
                      const raw = row[col.key];
                      const value = col.isAmount ? numberFmt(raw) : raw;
                      const baseTd = `px-3 py-4 align-middle text-center text-gray-800 whitespace-nowrap ${col.isAmount ? 'text-right tabular-nums' : 'text-left'}`;
                      const content = col.truncate ? (
                        <span className="inline-block max-w-full truncate" title={value}>{value}</span>
                      ) : (
                        value
                      );
                      return (
                        <td key={col.key} className={`${baseTd} ${col.width || ''}`}>
                          {content}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default PGTransactionHistory;
